import React, { useMemo } from 'react';
import { mergePages } from '@vibesites/runtime';
import type { ComponentContentSchema, PageDataMap } from '@vibesites/runtime';
import { EditableToken, useCollectionBinding } from '@vibesites/runtime/react';
import pagesData from '../pages.json';
import basePagesData from '@vibesites/theme-prestige/pages.json';

interface VirtualTour {
  virtual_tour_url?: string | null;
}

/**
 * This theme's pages.json carries only aliases it overrides or adds; every
 * other page is inherited. The deploy pipeline shallow-merges that chain at
 * build time, but this component reads pages.json statically, so we reproduce
 * the merge here (base → this theme) and then honor page settings (`visible`,
 * `noindex`) so sitemap tiles match what the editor configured.
 */
const resolvedPages = mergePages(
  basePagesData as PageDataMap,
  pagesData as PageDataMap,
);

export function SitemapContent() {
  const virtualTours = useCollectionBinding<VirtualTour>('sitemapcontent.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  const pages = useMemo(
    () =>
      resolvedPages.filter((p) => {
        if (p.path === '/sitemap' || p.path.includes('{{')) return false;
        if (p.template) return false;
        if (p.visible === false || p.noindex) return false;
        if (p.path === '/virtual-tour' && !hasVirtualTours) return false;
        return true;
      }),
    [hasVirtualTours],
  );

  return (
    <section className="py-16 md:py-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableToken
          path="sitemapcontent.heading"
          as="h1"
          className="font-heading text-3xl font-normal text-foreground mb-10 text-center"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {pages.map((page) => (
            <a
              key={page.path}
              href={page.path}
              className="block border border-[var(--vibe-color-text)] rounded px-6 py-4 text-center font-body text-sm text-foreground hover:bg-[var(--vibe-color-primary)] hover:text-[var(--vibe-color-text-on-primary)] hover:border-[var(--vibe-color-text-on-primary)] transition-colors"
            >
              {page.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SitemapContent',
  displayName: 'Sitemap',
  tokens: {
    'sitemapcontent.heading': {
      label: 'Page Heading',
      type: 'text',
      defaultValue: 'Sitemap',
    },
  },
  collectionBindings: {
    'sitemapcontent.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(SitemapContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SitemapContent;
